<template>
  <MDBSideNav right v-model="viewEmployeeListDrawer" mode="push" contentSelector="#ProjectTagging" :sidenavWidth="354">
    <div :class="[selectedEmployees.length > 0 ? '' : 'hide-selection-info', 'report-select-employee-container']">
      <div class="header">
        <IconArrowLeft class="icon" @click="handleAllDrawerClose(false)" />
        <h6>Select Employee</h6>
        <IconX class="icon x" @click="handleAllDrawerClose(true)" />
      </div>
      <div class="filter">
        <SPASearchbar v-model.trim="reportSelectEmployeeSearchString" @keydown.stop="handleKeyPress" @clickSearch="handleSearch" placeholder="Search" />
      </div>
      <div class="selection-info" v-show="selectedEmployees.length > 0">
        <div class="selected-counter">
          <SPALozenge :text="selectedEmployees.length" />
          <span>SELECTED</span>
        </div>
        <span class="selection-reset" @click="resetSelection">Reset</span>
      </div>
      <div class="employee-list" :class="{ 'is-loading-full': isLoadingFull, 'is-loading-scroll': isLoadingScroll }">
        <MDBScrollbar ref="employeeListDOM">
          <template v-if="noMatchingSearch && !isLoading">
            <div class="empty-container">
              <div>
                <h5>No Results Found</h5>
              </div>
            </div>
          </template>
          <template v-else-if="isEmployeeListEmpty && !isLoading">
            <div class="empty-container">
              <img src="@/assets/images/2024/no-employees.png" alt="no-employees" />
              <div>
                <h5>No Employees Assigned</h5>
                Go to Project List, click the kebab menu and select “Manage Assignees” to start assigning employees in this project.
              </div>
            </div>
          </template>
          <template v-for="(group, key) in groupedEmployees" :key="key">
            <div class="group-header">{{ key }}</div>
            <template v-for="employee in group" :key="employee.employeeIdNo">
              <div class="list-row" @click="selectEmployee(employee)" :class="{ selected: findEmployeeFromSelection(employee) }">
                <div class="list-row-container text-truncate">
                  <div class="fullname text-truncate">{{ employee.lastName }}, {{ employee.firstName }}</div>
                  <div class="employee-id-number">{{ employee.employeeIdNo }}</div>
                  <div class="members-status">{{ employee.employmentStatus }}</div>
                </div>
                <IconCheck v-if="findEmployeeFromSelection(employee)" class="list-row-icon" />
              </div>
            </template>
          </template>
          <MDBSpinner v-if="isLoading" />
        </MDBScrollbar>
      </div>
      <div class="action">
        <SPAButton color="success" :ripple="false" @click.stop.prevent="confirmSelection"> Confirm </SPAButton>
      </div>
    </div>
  </MDBSideNav>
</template>

<script>
import { MDBSideNav, MDBScrollbar, MDBSpinner } from 'mdb-vue-ui-kit';
import { storeToRefs } from 'pinia';
import { useProjectTaggingStore } from '../../../../stores/projectTagging';
import IconArrowLeft from '~icons/ph/arrow-left';
import IconX from '~icons/ph/x';
import IconCheck from '~icons/ph/check';
import { ref, computed, watch } from 'vue';
import SPASearchbar from '../../../../components/SPASearchbar.vue';
import SPALozenge from '../../../../components/SPALozenge.vue';
import { useInfiniteScroll, useDebounceFn } from '@vueuse/core';
import { GetProjectEmployees } from '../../../../services/ProjectTagging/GetProjectEmployees';
import SPAButton from '../../../../components/SPAButton.vue';

export default {
  name: 'ProjectTaggingReportSelectEmployee',
  components: {
    MDBSideNav,
    IconArrowLeft,
    IconX,
    SPASearchbar,
    MDBScrollbar,
    MDBSpinner,
    SPALozenge,
    IconCheck,
    SPAButton,
  },
  setup() {
    const projectTaggingStore = useProjectTaggingStore();
    const { viewEmployeeListDrawer, drawerReportState, confirmedProject, confirmedEmployees, selectedEmployees } = storeToRefs(projectTaggingStore);
    const reportSelectEmployeeSearchString = ref('');
    const employeeList = ref([]);
    const currentPage = ref(1);
    const totalItems = ref(0);
    const totalPages = ref(0);
    const isLoading = ref(true);
    const defaultParams = { SortColumn: 'lastname', SortOrder: 'asc' };
    const groupedEmployees = computed(() => {
      return employeeList.value.reduce((groups, employee) => {
        let firstLetter = employee.lastName.charAt(0).toUpperCase();
        if (!groups[firstLetter]) {
          groups[firstLetter] = [];
        }
        let entry = groups[firstLetter];
        entry.push(employee);
        return groups;
      }, {});
    });

    const isLoadingFull = computed(() => isLoading.value && employeeList.value.length === 0);
    const isLoadingScroll = computed(() => isLoading.value && employeeList.value.length > 0);
    const noMatchingSearch = ref(false);
    const handleSearch = async () => {
      isLoading.value = true;
      employeeList.value = [];
      totalItems.value = 0;
      const params = {
        SearchString: reportSelectEmployeeSearchString.value,
        ProjectID: confirmedProject.value.id,
        RowsPerPage: 50,
        ...defaultParams,
      };
      const result = await GetProjectEmployees(params);
      currentPage.value = 1;
      employeeList.value = result.data;
      totalItems.value = result.pagination.totalItems;
      isLoading.value = false;
      noMatchingSearch.value = reportSelectEmployeeSearchString.value != '' && employeeList.value.length < 1 ? true : false;
    };

    const handleKeyPress = () => {
      isLoading.value = true;
      debouncedSearch();
    };
    const debouncedSearch = useDebounceFn(() => {
      handleSearch();
    }, 1000);

    const confirmSelection = () => {
      confirmedEmployees.value = selectedEmployees.value;
      viewEmployeeListDrawer.value = false;
      resetSelection();
    };

    const resetSelection = () => {
      selectedEmployees.value = [];
    };
    const selectEmployee = (employee) => {
      const found = findEmployeeFromSelection(employee);
      if (!found) {
        selectedEmployees.value.push({ ...employee });
      } else {
        selectedEmployees.value = selectedEmployees.value.filter((el) => el.employeeId !== employee.employeeId);
      }
    };
    const findEmployeeFromSelection = (employee) => {
      return selectedEmployees.value.find((el) => el.employeeId === employee.employeeId);
    };

    const employeeListDOM = ref();
    useInfiniteScroll(
      employeeListDOM,
      async () => {
        if (currentPage.value === totalPages.value) {
          return;
        }
        isLoading.value = true;
        currentPage.value += 1;
        const params = {
          ProjectID: confirmedProject.value.id,
          PageNumber: currentPage.value,
          RowsPerPage: 50,
          ...defaultParams,
        };
        const result = await GetProjectEmployees(params);
        employeeList.value = employeeList.value.concat(result.data);
        totalItems.value = result.pagination.totalItems;
        isLoading.value = false;
      },
      { distance: 10 }
    );
    const handleAllDrawerClose = (closeAll) => {
      resetSelection();
      if (!closeAll) {
        viewEmployeeListDrawer.value = false;
        return;
      }
      drawerReportState.value = false;
      viewEmployeeListDrawer.value = false;
    };

    const isEmployeeListEmpty = computed(() => employeeList.value.length < 1);

    watch(confirmedProject, async () => {
      isLoading.value = true;
      const params = {
        ProjectID: confirmedProject.value.id,
        RowsPerPage: 50,
        ...defaultParams,
      };
      const result = await GetProjectEmployees(params);
      employeeList.value = result.data;
      totalItems.value = result.pagination.totalItems;
      totalPages.value = result.pagination.totalPages;
      currentPage.value = result.pagination.currentPage;
      resetSelection();
      isLoading.value = false;
    });

    return {
      viewEmployeeListDrawer,
      reportSelectEmployeeSearchString,
      handleSearch,
      confirmSelection,
      groupedEmployees,
      isLoading,
      resetSelection,
      employeeListDOM,
      isLoadingFull,
      isLoadingScroll,
      selectedEmployees,
      selectEmployee,
      findEmployeeFromSelection,
      handleAllDrawerClose,
      isEmployeeListEmpty,
      handleKeyPress,
      noMatchingSearch,
    };
  },
};
</script>

<style lang="scss" scoped>
.report-select-employee-container.hide-selection-info {
  grid-template-rows: auto auto 1fr auto;
}
.report-select-employee-container {
  display: grid;
  line-height: 24px;
  flex-direction: column;
  grid-template-rows: auto auto auto 1fr auto;
  height: 100%;
}
.header,
.filter,
.selection-info,
.employee-list,
.action {
  padding: 14px 16px;
  border: 1px solid $neutral-40;
  border-right: 0;
  border-top: 0;
}
.header {
  display: flex;
  align-items: center;
  gap: 10px;

  h6 {
    margin: 0;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: $neutral-800;
  }
  .icon {
    width: 16px;
    height: 16px;
    cursor: pointer;

    &:hover {
      color: $green-300;
    }
  }
  .x {
    margin-left: auto;
  }
}
.selection-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 16px;
  .selected-counter {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    font-size: 12px;

    span {
      color: $neutral-400;
      line-height: 12px;
      letter-spacing: 0.04em;
    }
    .spa-lozenge {
      display: inline-flex;
      align-items: center;
      background-color: $blue-300;
      color: $neutral-0;
      pointer-events: none;
      padding-inline: 12px;
      border-radius: 12px;
      line-height: 16px;
    }
  }
  .selection-reset {
    font-weight: 500;
    font-size: 12px;
    padding: 2px 8px;
    line-height: 12px;
    color: $neutral-300;
    cursor: pointer;
  }
}
.employee-list {
  overflow-y: auto;
  padding-inline: 0;

  &.is-loading-full .ps {
    display: grid;
    place-content: center;
  }
  &.is-loading-scroll .spinner-border {
    margin-block: 10px;
    margin-inline: auto;
    display: block;
  }
  .group-header {
    padding-inline: 16px;
    font-size: 12px;
    color: $neutral-200;
  }
  .spinner-border {
    border-color: $neutral-300;
    border-right-color: transparent;
  }
  .list-row {
    display: grid;
    grid-template-columns: 3fr auto;
    align-items: center;
    padding-inline: 16px;

    &:hover,
    &.selected {
      background-color: $green-50;
      cursor: pointer;
    }

    .list-row-container {
      padding: 12px 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .list-row-icon {
      color: $green-300;
    }
  }
  .fullname {
    color: $neutral-800;
  }
  .employee-id-number {
    color: $neutral-400;
    font-size: 12px;
  }
  .members-status {
    font-size: 12px;
    line-height: 16px;
    text-transform: capitalize;
    color: $neutral-400;
  }
}
.action button {
  width: 100%;
}
.empty-container {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  padding: 24px;
  text-align: center;
  div {
    display: grid;
    justify-items: center;
    line-height: 24px;
    font-size: 14px;
    h5 {
      font-weight: 500;
    }
  }
}
</style>
